const EXPORT_REMOVE_SELECTOR = [
  "[data-export-exclude]",
  "[data-report-block-handle]",
  "[data-report-ui]",
  "script",
  "style",
].join(",");

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const normalizeText = (value) =>
  String(value || "").replace(/\s+/g, " ").trim().toLowerCase();

const formatDate = (value) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(value instanceof Date ? value : new Date(value));
  } catch {
    return "";
  }
};

const replaceWithSpan = (node, className, text) => {
  const doc = node.ownerDocument || document;
  const span = doc.createElement("span");
  if (className) span.className = className;
  span.textContent = text || node.textContent || "";
  node.replaceWith(span);
  return span;
};

const appendSourceUrl = (span, url) => {
  if (!url) return;
  const visible = normalizeText(span.textContent).includes(normalizeText(url));
  if (visible) return;
  const doc = span.ownerDocument || document;
  const urlSpan = doc.createElement("span");
  urlSpan.className = "source-url";
  urlSpan.textContent = ` ${url}`;
  span.appendChild(urlSpan);
};

const sanitizeReportClone = (clone, title) => {
  clone.querySelectorAll(EXPORT_REMOVE_SELECTOR).forEach((node) => node.remove());

  clone.querySelectorAll("[data-report-citation]").forEach((node) => {
    replaceWithSpan(
      node,
      "citation-ref",
      node.getAttribute("data-export-label") || node.textContent?.trim() || "",
    );
  });

  clone.querySelectorAll("[data-report-source-link]").forEach((node) => {
    const span = replaceWithSpan(
      node,
      "source-title",
      node.textContent?.trim() || node.getAttribute("title") || "Source",
    );
    appendSourceUrl(span, node.getAttribute("data-export-url") || node.getAttribute("title") || "");
  });

  // Any remaining buttons are interactive UI. Preserve their readable text but
  // remove browser button styling from the export DOM.
  clone.querySelectorAll("button").forEach((node) => {
    replaceWithSpan(node, "button-text", node.textContent?.trim() || "");
  });

  clone.querySelectorAll("a[href]").forEach((link) => {
    const href = link.getAttribute("href");
    if (!href) return;
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });

  const firstHeading = clone.querySelector("h1");
  if (firstHeading && normalizeText(firstHeading.textContent) === normalizeText(title)) {
    firstHeading.remove();
  }
};

const buildFallbackSources = (citations = []) => {
  if (!Array.isArray(citations) || citations.length === 0) return "";
  const items = citations
    .map((citation) => {
      const index = escapeHtml(citation?.index || "");
      const title = escapeHtml(citation?.title || citation?.url || `Source ${index}`);
      const url = citation?.url ? `<span class="source-url"> ${escapeHtml(citation.url)}</span>` : "";
      return `<li><span class="source-index">[${index}]</span><span class="source-title">${title}${url}</span></li>`;
    })
    .join("");
  return `<section class="source-appendix" data-report-sources="true"><h4>Sources</h4><ol>${items}</ol></section>`;
};

const PRINT_CSS = `
  @page {
    size: A4;
    margin: 18mm 16mm 20mm;
  }

  * { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    color: #18181b;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 11.5pt;
    line-height: 1.62;
  }

  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  .print-shell {
    max-width: 760px;
    margin: 0 auto;
  }

  .print-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #d4d4d8;
  }

  .print-kicker {
    margin: 0 0 6px;
    color: #71717a;
    font-size: 8pt;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .print-title {
    margin: 0;
    color: #111827;
    font-size: 23pt;
    line-height: 1.16;
    font-weight: 720;
    letter-spacing: -0.02em;
  }

  .print-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
    margin-top: 10px;
    color: #71717a;
    font-size: 8.5pt;
  }

  .print-body { font-size: 11.5pt; }

  .print-body [data-report-block-handle],
  .print-body [data-report-ui],
  .print-body [data-export-exclude],
  .print-body button {
    display: none !important;
  }

  .print-body h1,
  .print-body h2,
  .print-body h3,
  .print-body h4 {
    color: #18181b;
    break-after: avoid;
    page-break-after: avoid;
  }

  .print-body h1 {
    margin: 0 0 13px;
    font-size: 19pt;
    line-height: 1.22;
    font-weight: 700;
  }

  .print-body h2 {
    margin: 24px 0 9px;
    padding-bottom: 5px;
    border-bottom: 1px solid #e4e4e7;
    font-size: 15.5pt;
    line-height: 1.3;
    font-weight: 690;
  }

  .print-body h3 {
    margin: 18px 0 7px;
    font-size: 12.5pt;
    line-height: 1.35;
    font-weight: 680;
  }

  .print-body h4 {
    margin: 14px 0 6px;
    color: #3f3f46;
    font-size: 10.5pt;
    line-height: 1.35;
    font-weight: 680;
  }

  .print-body p {
    margin: 0 0 10px;
    orphans: 3;
    widows: 3;
  }

  .print-body ul,
  .print-body ol {
    margin: 0 0 11px 20px;
    padding: 0;
  }

  .print-body li { margin: 2px 0 4px; }

  .print-body blockquote {
    margin: 14px 0;
    padding: 8px 12px;
    border-left: 3px solid #94a3b8;
    background: #f8fafc;
    color: #475569;
    font-style: italic;
    break-inside: avoid;
  }

  .print-body table {
    width: 100%;
    margin: 14px 0;
    border-collapse: collapse;
    break-inside: avoid;
    page-break-inside: avoid;
    font-size: 9.5pt;
  }

  .print-body th,
  .print-body td {
    border: 1px solid #d4d4d8;
    padding: 6px 8px;
    vertical-align: top;
  }

  .print-body th {
    background: #f4f4f5;
    color: #3f3f46;
    font-size: 8.5pt;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .print-body tr:nth-child(even) td { background: #fafafa; }

  .print-body pre,
  .print-body code {
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  }

  .print-body pre {
    margin: 13px 0;
    padding: 10px 12px;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    border: 1px solid #e4e4e7;
    border-radius: 8px;
    background: #f8fafc;
    color: #27272a;
    font-size: 8.8pt;
    line-height: 1.45;
    break-inside: avoid;
  }

  .print-body code {
    border-radius: 4px;
    background: #f4f4f5;
    padding: 1px 4px;
    font-size: 9pt;
  }

  .print-body pre code {
    background: transparent;
    padding: 0;
  }

  .print-body a {
    color: #1d4ed8;
    text-decoration: none;
    overflow-wrap: anywhere;
  }

  .print-body hr {
    margin: 20px 0;
    border: 0;
    border-top: 1px solid #d4d4d8;
  }

  .print-body img,
  .print-body svg {
    max-width: 100%;
    height: auto;
    break-inside: avoid;
  }

  .citation-ref {
    color: #1d4ed8;
    font-size: 0.78em;
    font-weight: 650;
    white-space: nowrap;
    vertical-align: super;
  }

  [data-report-sources],
  .source-appendix {
    margin-top: 28px;
    padding-top: 14px;
    border-top: 1px solid #d4d4d8;
    break-before: auto;
  }

  [data-report-sources] h4,
  .source-appendix h4 {
    margin: 0 0 8px;
    color: #52525b;
    font-size: 8.5pt;
    font-weight: 750;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  [data-report-sources] ol,
  .source-appendix ol {
    margin-left: 0;
    padding-left: 0;
    list-style: none;
  }

  [data-report-sources] li,
  .source-appendix li {
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr);
    gap: 8px;
    margin: 0 0 6px;
    color: #3f3f46;
    font-size: 8.8pt;
    line-height: 1.35;
    break-inside: avoid;
  }

  .source-index,
  [data-report-sources] li > span:first-child {
    color: #71717a;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  }

  .source-url {
    display: block;
    margin-top: 2px;
    color: #71717a;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;

export const buildReportPrintHtml = ({
  title,
  contentElement,
  citations = [],
  generatedAt = new Date(),
  companyName,
} = {}) => {
  if (!contentElement) return "";

  const clone = contentElement.cloneNode(true);
  const firstHeadingTitle = clone.querySelector("h1")?.textContent?.trim();
  const displayTitle =
    title && title !== "Untitled Report" ? title : firstHeadingTitle || title || "Report";

  sanitizeReportClone(clone, displayTitle);

  const hasSources = Boolean(clone.querySelector("[data-report-sources]"));
  const fallbackSources = hasSources ? "" : buildFallbackSources(citations);
  const safeTitle = escapeHtml(displayTitle);
  const generatedLabel = formatDate(generatedAt);
  const meta = [
    companyName ? `Case: ${escapeHtml(companyName)}` : "",
    generatedLabel ? `Exported: ${escapeHtml(generatedLabel)}` : "",
  ].filter(Boolean);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${safeTitle}</title>
  <style>${PRINT_CSS}</style>
</head>
<body>
  <article class="print-shell">
    <header class="print-header">
      <p class="print-kicker">Report</p>
      <h1 class="print-title">${safeTitle}</h1>
      ${meta.length ? `<div class="print-meta">${meta.map((item) => `<span>${item}</span>`).join("")}</div>` : ""}
    </header>
    <main class="print-body">
      ${clone.innerHTML}
      ${fallbackSources}
    </main>
  </article>
</body>
</html>`;
};
